'use client'

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { IoChevronDown } from 'react-icons/io5'
import { RiCheckLine } from '@remixicon/react'

export interface FilterSelectOption {
  value: string
  label: string
  disabled?: boolean
}

interface FilterSelectProps {
  options: (FilterSelectOption | string)[]
  value: string
  onChange: (value: string) => void
  /** Every one of these sits unlabelled next to its table, so name it. */
  ariaLabel: string
  placeholder?: string
  /** Width utilities for the wrapper, e.g. `w-full sm:w-40`. */
  className?: string
}

const toOption = (o: FilterSelectOption | string): FilterSelectOption =>
  typeof o === 'string' ? { value: o, label: o } : o

const PANEL_MAX_HEIGHT = 280
const PANEL_GAP = 4

/** Where the panel goes, in viewport coordinates. */
interface PanelRect {
  left: number
  top: number
  width: number
  maxHeight: number
}

/**
 * The dashboard's filter dropdown.
 *
 * Two controls, chosen by CSS rather than by a media-query hook, because which
 * one appears must not depend on a client effect resolving:
 *
 *  - Touch (< md): a real `<select>`. The OS picker cannot be clipped by a
 *    parent, needs no JS, and is what a phone user expects. Its list is drawn
 *    by the platform, so it is not ours to style.
 *  - Pointer (>= md): a listbox we draw, so the closed control and the open
 *    list share the dashboard's type, spacing and hairline borders instead of
 *    the browser's default menu.
 *
 * The list renders through a portal: these filters sit inside cards that clip
 * their overflow, which would cut an absolutely positioned panel in half.
 */
export function FilterSelect({
  options,
  value,
  onChange,
  ariaLabel,
  placeholder,
  className = '',
}: FilterSelectProps) {
  const items = options.map(toOption)
  const selected = items.find((o) => o.value === value)

  return (
    <div className={`relative inline-flex items-center ${className}`}>
      {/* Touch: the platform picker. */}
      <select
        aria-label={ariaLabel}
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
        // 44px/16px: under either, iOS zooms the page when the control takes
        // focus.
        className="md:hidden h-11 w-full appearance-none rounded-lg border border-dashboard-hairline bg-white pl-3 pr-8 text-[16px] font-medium text-dashboard-text font-poppins cursor-pointer focus:outline-none focus:border-[#B9C6D2]"
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {items.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      <IoChevronDown className="md:hidden pointer-events-none absolute right-2.5 w-4 h-4 text-dashboard-text" />

      <DesktopListbox
        ariaLabel={ariaLabel}
        items={items}
        value={value}
        label={selected?.label ?? placeholder ?? ''}
        onChange={onChange}
      />
    </div>
  )
}

function DesktopListbox({
  ariaLabel,
  items,
  value,
  label,
  onChange,
}: {
  ariaLabel: string
  items: FilterSelectOption[]
  value: string
  label: string
  onChange: (value: string) => void
}) {
  const listId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [rect, setRect] = useState<PanelRect | null>(null)
  const [active, setActive] = useState(0)

  const place = useCallback(() => {
    const el = triggerRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const below = window.innerHeight - r.bottom - PANEL_GAP - 8
    const above = r.top - PANEL_GAP - 8
    // Open upwards when the space below cannot hold a usable list.
    const flip = below < Math.min(PANEL_MAX_HEIGHT, 160) && above > below
    const maxHeight = Math.min(PANEL_MAX_HEIGHT, flip ? above : below)
    setRect({
      left: r.left,
      top: flip ? r.top - PANEL_GAP - maxHeight : r.bottom + PANEL_GAP,
      width: Math.max(r.width, 160),
      maxHeight,
    })
  }, [])

  useLayoutEffect(() => {
    if (open) place()
  }, [open, place])

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return
      setOpen(false)
    }
    // The panel is fixed to the viewport, so anything that moves the trigger
    // has to close it rather than leave it stranded. Scrolling the list itself
    // is not that: it also reaches this capture-phase listener, and closing on
    // it makes a long option list impossible to scroll through.
    const onScroll = (event: Event) => {
      const target = event.target as Node | null
      if (target && panelRef.current?.contains(target)) return
      setOpen(false)
    }
    const onDismiss = () => setOpen(false)

    document.addEventListener('mousedown', onPointerDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onDismiss)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onDismiss)
    }
  }, [open])

  const openList = () => {
    const index = items.findIndex((o) => o.value === value)
    setActive(index < 0 ? 0 : index)
    setOpen(true)
  }

  const commit = (option: FilterSelectOption) => {
    if (option.disabled) return
    onChange(option.value)
    setOpen(false)
    triggerRef.current?.focus()
  }

  const step = (delta: number) => {
    setActive((current) => {
      let next = current
      for (let i = 0; i < items.length; i += 1) {
        next = (next + delta + items.length) % items.length
        if (!items[next]?.disabled) return next
      }
      return current
    })
  }

  const onKeyDown = (event: ReactKeyboardEvent) => {
    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault()
        openList()
      }
      return
    }
    if (event.key === 'Escape' || event.key === 'Tab') {
      setOpen(false)
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      step(1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      step(-1)
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      const option = items[active]
      if (option) commit(option)
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-haspopup="listbox"
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
        // Built to the same spec as DatePickerModal's trigger, which sits right
        // beside it in these headers: 30px tall, px-3, hairline gray-200, and an
        // xs/medium/gray-900 label. min-width stops the control resizing as the
        // chosen option changes.
        className={`hidden md:flex h-[30px] w-full min-w-[140px] items-center justify-between gap-1.5 rounded-lg border bg-white px-3 py-0 cursor-pointer transition-colors hover:bg-slate-50 focus:outline-none ${
          open ? 'border-[#8FA6BC]' : 'border-gray-200'
        }`}
      >
        <span className="text-xs font-medium text-gray-900 font-poppins leading-[160%] truncate">
          {label}
        </span>
        <IoChevronDown
          className={`w-3.5 h-3.5 shrink-0 text-gray-500 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && rect && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              id={listId}
              role="listbox"
              aria-label={ariaLabel}
              style={{
                position: 'fixed',
                left: rect.left,
                top: rect.top,
                width: rect.width,
                maxHeight: rect.maxHeight,
                zIndex: 300,
              }}
              className="overflow-y-auto overscroll-contain rounded-xl border border-[#E3E9EF] bg-white p-1 shadow-[var(--dashboard-shadow-popover)]"
            >
              {items.map((option, index) => {
                const isSelected = option.value === value
                return (
                  <div
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={option.disabled}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => commit(option)}
                    className={`flex h-8 items-center justify-between gap-2 rounded-lg px-2.5 text-[13px] font-poppins transition-colors ${
                      option.disabled
                        ? 'cursor-not-allowed text-[#B0B7C3]'
                        : 'cursor-pointer text-dashboard-text'
                    } ${index === active && !option.disabled ? 'bg-[#F1F5F9]' : ''} ${
                      isSelected ? 'font-semibold' : 'font-medium'
                    }`}
                  >
                    <span className="truncate">{option.label}</span>
                    {isSelected && <RiCheckLine className="w-4 h-4 shrink-0 text-dashboard-accent" />}
                  </div>
                )
              })}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

export default FilterSelect
